import React from 'react';
import {
  Badge, Box, Divider, Flex, HStack, IconButton, Skeleton, Text, Tooltip, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiBookOpen, FiChevronDown, FiTrendingUp, FiFileText, FiAlertCircle, FiTool, FiFolder, FiLayers, FiCheckCircle, FiMaximize2, FiTarget,
  FiPause, FiSquare, FiExternalLink,
} from 'react-icons/fi';
import { useReducedMotion } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import useDashboardMetrics from '../../hooks/useDashboardMetrics';
import useCurrentUser from '../../hooks/useCurrentUser';
import { useAppearance } from '../../theme/AppearanceProvider';
import { useDashboardExpand } from './DashboardExpandContext';
import OnlineNow from '../OnlineNow';
import useTimeClock, { parseUtcMs } from '../../hooks/useTimeClock'; // item 1343b — the strip-wide timer bar
import { fmtElapsed } from '../TimerChip';
import NotificationBell from '../NotificationBell';
import { compactMoney } from '../../utils/money'; // item 327 — stealth-aware
import { ACCESS_ROLE_LABELS } from '../../models/user'; // item 850 — role label for external reps
import KpiBreakdownModal from './KpiBreakdownModal'; // item 1481 — click a tile → how the number is derived

interface Kpi {
  key: string;
  label: string;
  value: string;
  icon: React.ReactNode;
  accent: string;
  hint?: string; // item 1147 (Chad) — plain-language hover explaining the number
}

// The always-visible KPI strip (item 96) — rides at the top of every page's content.
// Collapsed it shows four headline numbers; the chevron expands the full dashboard
// bento inline (accordion). Skin-aware (glassy on Aurora, flat on Accountant) and
// honours prefers-reduced-motion (no height animation when the OS asks for less).
const KpiStrip: React.FC = () => {
  const { aurora } = useAppearance();
  const history = useHistory();
  const reduceMotion = useReducedMotion();
  const m = useDashboardMetrics();
  // item 1481 — which tile's breakdown is open
  const [breakdownKey, setBreakdownKey] = React.useState<string | null>(null);
  const tileCursor = (k: Kpi): string | undefined => {
    if (m.breakdowns?.[k.key]) return 'pointer';
    return k.hint ? 'help' : undefined;
  };
  // item 102: money-restricted roles (Project Lead / Deployment / Dev / Intern) get a
  // project-focused strip with NO dollar figures + no financial bento expand.
  const {
    hasFinancialAccess, isOutsideSales, user, accessRole,
  } = useCurrentUser();
  // item 258 — open/peek state is shared via context; the expanded bento renders in
  // DashboardBentoSlot, in normal flow BELOW this sticky strip (not pinned on top of it).
  const { open, toggle } = useDashboardExpand();

  const glassBg = useColorModeValue('whiteAlpha.700', 'whiteAlpha.100');
  const bg = aurora ? glassBg : 'ledgerSurface';
  const borderColor = useColorModeValue('blackAlpha.200', 'whiteAlpha.200');
  const labelColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  // item 958 — light-mode contrast: the strip's hairline edge + drop shadow were the DARK-canvas recipe
  // (white edge, heavy shadow) in BOTH color modes → no visible edge / a muddy halo on the light
  // canvas. Dark keeps today's exact values.
  const shellBorder = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const shellShadow = useColorModeValue(
    '0 8px 24px rgba(0,0,0,0.10)',
    '0 10px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.18)',
  );
  // item 958 — light-mode contrast: the pale .300 icon accents (brandBlue.300 / purple.300) wash out on
  // light glass; step them to .500 in light mode only (dark unchanged).
  const darkMode = useColorModeValue(false, true);
  const iconAccent = (a: string) => (darkMode ? a : a.replace(/\.300$/, '.500'));

  // item 469 — static-overhead coverage: how much MRR still needs to cover the monthly
  // overhead (annual budget ÷ 12). Color ramps by coverage fraction: Red <1/3, Orange <2/3,
  // Yellow <fully covered, Green fully covered. Only shown once a budget is configured.
  const coverageAccent = (() => {
    if (m.overheadCoverage >= 1) return 'brandGreen.400';
    if (m.overheadCoverage >= 2 / 3) return 'yellow.400';
    if (m.overheadCoverage >= 1 / 3) return 'orange.400';
    return 'red.400';
  })();
  const overheadTile: Kpi = {
    key: 'overhead',
    label: 'Overhead',
    value: m.overheadCoverage >= 1 ? 'Covered' : compactMoney(m.overheadRemaining),
    icon: <FiTarget />,
    accent: coverageAccent,
    // item 1147 — the number people kept guessing at
    hint: `Monthly overhead is Budget+ ÷ 12. Budget+ = your static annual budget${m.fyCommissions > 0 ? ` (${compactMoney(m.annualStaticBudget)})` : ''} plus every commission accrued this FY${m.fyCommissions > 0 ? ` (${compactMoney(m.fyCommissions)})` : ''} — reversing a commission drops it back out. This shows the slice recurring revenue does NOT cover. "Covered" = MRR alone pays the overhead.`,
  };
  // item 1132 (Chad) — THIS month's break-even pulse: finalized money (invoices + sub
  // charges) vs the monthly overhead share. Green over, red under — the always-visible
  // "did we break even this month" signal.
  const monthDelta = m.monthRevenue - m.monthlyOverhead;
  const breakEvenTile: Kpi = {
    key: 'breakeven',
    label: 'Break-even (mo)',
    value: `${monthDelta >= 0 ? '+' : '−'}${compactMoney(Math.abs(monthDelta))}`,
    icon: <FiTarget />,
    // item 1228 (Chad: "positive being red is throwing me off") — SEMANTIC green, never the
    // brand scale: the Pulse rebrand made the accent RED, so brandGreen.400 generates red
    // and a surplus painted red. Literal green regardless of brand.
    accent: monthDelta >= 0 ? 'green.400' : 'red.400',
    // item 1147 — green + = this month already covered its overhead share by this much;
    // red − = still short by this much
    hint: `This month's finalized money (invoices + subscription charges) minus the month's Budget+ share (static budget + FY commissions, ÷ 12). Green + = surplus after overhead; red − = still short this month.`,
  };

  const financialKpis: Kpi[] = [
    {
      key: 'pipeline',
      // item 1141 (Chad) — CONTRACTED money only (won-unbilled + AR + WOs + POs + drafts);
      // pending quotes moved to their own Opportunities tile below
      label: 'Pipeline',
      value: compactMoney(m.pipelineValue),
      icon: <FiTrendingUp />,
      accent: 'brandGreen.400',
      hint: 'Contracted money not yet in the bank: unbilled balances on won proposals + open AR (incl. subscriptions) + open service work orders + POs on order + drafts. Pending quotes and unconverted sales orders are NOT in here.',
    },
    {
      key: 'opportunities',
      label: 'Opportunities',
      value: compactMoney(m.pendingOpportunities),
      icon: <FiLayers />,
      accent: 'brandBlue.300',
      hint: 'Pending proposals + unconverted sales orders at full value — potential work awaiting a decision. Moves into Pipeline when won/converted.',
    },
    {
      // item 1155 (Chad) — the ALL-THINGS number: contracted + potential in one glance
      key: 'totalbook',
      label: 'Total Book',
      value: compactMoney(m.totalBook),
      icon: <FiBookOpen />,
      accent: 'purple.300',
      hint: 'Everything out there: Pipeline (contracted, unbilled) + Opportunities (pending quotes and sales orders). The whole board, won or not.',
    },
    {
      key: 'invoiced',
      label: 'Invoiced (FY)',
      value: compactMoney(m.collected),
      icon: <FiFileText />,
      accent: 'brandBlue.300',
    },
    {
      key: 'ar',
      label: 'Outstanding AR',
      value: compactMoney(m.outstanding),
      icon: <FiAlertCircle />,
      accent: 'brandBlue.300',
    },
    {
      key: 'paid',
      label: 'Paid (FY)',
      value: compactMoney(m.paidValue),
      icon: <FiCheckCircle />,
      accent: 'brandGreen.400',
    },
    {
      key: 'open',
      label: 'Open work',
      value: String(m.openWO + m.pending),
      icon: <FiTool />,
      accent: 'brandGreen.400',
    },
    // item 469 — overhead coverage, only once an annual static budget is set
    ...(m.annualStaticBudget > 0 ? [overheadTile, breakEvenTile] : []),
  ];
  // money-free strip for project-management roles (item 102)
  const pmKpis: Kpi[] = [
    {
      key: 'projects', label: 'Projects', value: String(m.projectCount), icon: <FiFolder />, accent: 'brandGreen.400',
    },
    {
      key: 'proposals', label: 'Proposals', value: String(m.proposalCount), icon: <FiFileText />, accent: 'brandBlue.300',
    },
    {
      key: 'workorders', label: 'Work orders', value: String(m.woCount), icon: <FiLayers />, accent: 'brandBlue.300',
    },
    {
      key: 'open', label: 'Open work', value: String(m.openWO + m.pending), icon: <FiTool />, accent: 'brandGreen.400',
    },
  ];
  // item 850 — an external Outside Sales rep gets NO KPIs; the header just names who's logged in.
  let kpis: Kpi[];
  if (isOutsideSales) kpis = [];
  else kpis = hasFinancialAccess ? financialKpis : pmKpis;

  // item 1343b (Chad: "can the persistent timer cover the entire kpi bar, leaving who's online
  // and to the right visible") — while a timer RUNS, the tiles hand the whole row to the
  // clock; OnlineNow + the bell/expand cluster stay. Stop = pause (idle, resumable);
  // Stop & book opens the TimerChip's booking dialog via the shared window event.
  const {
    mine: timerMine, stop: timerStop, pause: timerPause, isBusy: timerBusy,
  } = useTimeClock();
  const runningClock = !isOutsideSales ? (timerMine?.running ?? null) : null;
  // item 1343c (Chad: "the timer is not changing") — the tick STATE is the wall clock itself,
  // so every second provably re-renders with a new value.
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!runningClock) return undefined;
    setNowMs(Date.now());
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [runningClock]);
  const runningElapsedMs = runningClock
    ? runningClock.accumulatedSeconds * 1000
      + (runningClock.segmentStartedAt ? Math.max(0, nowMs - parseUtcMs(runningClock.segmentStartedAt)) : 0)
    : 0;

  return (
    <Box mb={{ base: 3, md: 4 }}>
      <Flex
        align="center"
        gap={{ base: 2, md: 4 }}
        px={{ base: 3, md: 5 }}
        py={{ base: 2.5, md: 3 }}
        borderRadius="2xl"
        bg={bg}
        border="1px solid"
        borderColor={aurora ? shellBorder : 'ledgerStroke'} // item 958 — light-mode contrast
        backdropFilter={aurora ? 'var(--bx-glass-panel)' : undefined}
        boxShadow={aurora ? shellShadow : 'none'}
        overflowX="auto"
        // item 1089 — phone polish: tiles snap as you pan, so the strip reads as an
        // intentional carousel instead of a cut-off row
        sx={{ scrollbarWidth: 'none', '::-webkit-scrollbar': { display: 'none' }, scrollSnapType: 'x proximity' }}
      >
        {/* item 850 — external rep header: who's logged in + their role, no KPIs */}
        {isOutsideSales && (
          <HStack spacing={3} minW={0}>
            <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="extrabold" noOfLines={1}>
              {user?.name || 'Welcome'}
            </Text>
            {accessRole !== undefined && (
              <Badge colorScheme="brandGreen" borderRadius="full" px={2.5} py={1} fontSize="0.62rem">
                {ACCESS_ROLE_LABELS[accessRole]}
              </Badge>
            )}
          </HStack>
        )}
        {runningClock && (
          <HStack spacing={{ base: 3, md: 5 }} flex={1} minW={0}>
            <Badge colorScheme="green" fontSize="0.65rem" px={2} py={0.5} borderRadius="md">REC</Badge>
            <Text fontSize={{ base: 'xl', md: '2xl' }} fontFamily="mono" fontWeight="extrabold" lineHeight={1}>
              {fmtElapsed(runningElapsedMs)}
            </Text>
            <Text fontSize="sm" opacity={0.75} noOfLines={1} minW={0} flex={1}>
              {runningClock.note || 'Timer running'}
            </Text>
            <HStack spacing={2} flexShrink={0}>
              <Tooltip label="Stop — resume any time" hasArrow>
                <IconButton
                  aria-label="Stop"
                  size="sm"
                  variant="outline"
                  icon={<FiPause />}
                  isLoading={timerBusy}
                  onClick={() => timerPause(runningClock.id)}
                />
              </Tooltip>
              <Tooltip label="Stop & book" hasArrow>
                <IconButton
                  aria-label="Stop & book"
                  size="sm"
                  colorScheme="red"
                  icon={<FiSquare />}
                  isLoading={timerBusy}
                  onClick={async () => {
                    await timerStop(runningClock.id);
                    window.dispatchEvent(new CustomEvent('basix:timeclock-book', { detail: runningClock.id }));
                  }}
                />
              </Tooltip>
              {/* item 1346d (Chad) — pop-out goes STRAIGHT to the OS corner window (the click's
                  gesture rides the synchronous event into requestWindow). Auto-hides when you
                  return to the tab; grant the site "Automatically enter picture-in-picture"
                  and it auto-shows on tab-leave too. */}
              <Tooltip label="Pop out to your screen corner" hasArrow>
                <IconButton
                  aria-label="Pop out timer"
                  size="sm"
                  variant="ghost"
                  icon={<FiExternalLink />}
                  onClick={() => window.dispatchEvent(new CustomEvent('basix:timeclock-pip'))}
                />
              </Tooltip>
            </HStack>
          </HStack>
        )}
        {!runningClock && kpis.map((k, i) => (
          <React.Fragment key={k.key}>
            {i > 0 && <Divider orientation="vertical" h="34px" borderColor={aurora ? borderColor : 'ledgerStroke'} />}
            <Tooltip label={m.breakdowns?.[k.key] ? `${k.hint ?? ''}${k.hint ? ' ' : ''}Click for the breakdown.` : k.hint} isDisabled={!k.hint && !m.breakdowns?.[k.key]} placement="bottom" hasArrow openDelay={300}>
              <HStack
                spacing={2.5}
                minW="fit-content"
                flex={{ base: '0 0 auto', md: 1 }}
                sx={{ scrollSnapAlign: 'start' }}
                cursor={tileCursor(k)}
                onClick={() => { if (m.breakdowns?.[k.key]) setBreakdownKey(k.key); }} // item 1481
              >
                <Box color={aurora ? iconAccent(k.accent) : 'ledgerInk'} fontSize="18px" flexShrink={0}>{k.icon}</Box>
                <Box>
                  <Text
                    fontSize="0.62rem"
                    fontWeight="bold"
                    letterSpacing="0.12em"
                    textTransform="uppercase"
                    color={labelColor}
                    lineHeight="1"
                  >
                    {k.label}
                  </Text>
                  {m.isLoading ? (
                    <Skeleton mt={1} h="18px" w="56px" />
                  ) : (
                    <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="extrabold" lineHeight="1.2">
                      {k.value}
                    </Text>
                  )}
                </Box>
              </HStack>
            </Tooltip>
          </React.Fragment>
        ))}

        {/* item 318 — who's online right now (avatars), pinned to the right edge. item 850 — an
            external rep doesn't see internal presence. */}
        {/* item 1343c (Chad: "cleaner separation on the right") — a real divider fences the
            timer/tiles from the presence + action cluster */}
        {!isOutsideSales && (
          <Divider orientation="vertical" h="34px" ml="auto" borderColor={aurora ? borderColor : 'ledgerStroke'} />
        )}
        {!isOutsideSales && (
          <Box flexShrink={0}>
            <OnlineNow />
          </Box>
        )}

        {/* item 489 (NAV-2) — the right-edge action cluster gets its own spaced group with a
            gap from the online avatars, so the bell + dashboard buttons aren't crammed together */}
        <HStack spacing={2} ml={isOutsideSales ? 'auto' : 3} flexShrink={0}>
          {/* item 1110 (Chad) — the "N not enrolled" pill USED to live here. A global counter
              can't tell you WHICH project needs enrolling, so it nagged without pointing; the
              marker now rides the project's own row in the sidebar tree (NavButton's
              enrollmentPending), next to the in-project PENDING callout (item 737). */}
          {/* item 355a — global chat notification bell (all threads, one poll) */}
          <NotificationBell />

          {/* item 326 — the full-page LIVE dashboard (10s pulse; undock from there) */}
          {hasFinancialAccess && (
            /* item 357 — same expand language as the Sales Bible focus button (Chad) */
            <Tooltip label="Live dashboard (full page)" hasArrow>
              <IconButton
                aria-label="Open live dashboard"
                icon={<FiMaximize2 />}
                size="sm"
                variant="ghost"
                onClick={() => history.push('/dashboard/live')}
              />
            </Tooltip>
          )}
          {/* the expandable bento is financial — only money roles get it (item 102) */}
          {hasFinancialAccess && (
            <IconButton
              aria-label={open ? 'Collapse dashboard' : 'Expand dashboard'}
              title={open ? 'Hide dashboard' : 'Show full dashboard'}
              icon={(
                <Box
                  as={FiChevronDown}
                  transform={open ? 'rotate(180deg)' : 'rotate(0deg)'}
                  transition={reduceMotion ? undefined : 'transform 0.2s ease'}
                />
              )}
              size="sm"
              variant="ghost"
              onClick={toggle}
            />
          )}
        </HStack>
      </Flex>
      {/* item 258 — the expanded bento is rendered by DashboardBentoSlot OUTSIDE this
          sticky stack (below it, in normal flow), so it no longer floats over the page. */}
      {/* item 1481 — click a tile → how the number is derived */}
      <KpiBreakdownModal breakdown={breakdownKey ? (m.breakdowns?.[breakdownKey] ?? null) : null} onClose={() => setBreakdownKey(null)} isCount={breakdownKey === 'open'} />
    </Box>
  );
};

export default KpiStrip;
